/**
 * AlertsPage
 * Alerts dashboard with summary and alerts list
 */

import { useEffect } from 'react';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAlertsStore } from '../../stores/alerts-store';
import { AlertsSummarySection } from '../organisms/AlertsSummarySection';
import { AlertsTable } from '../organisms/AlertsTable';
import { SeverityFilter } from '../molecules/SeverityFilter';
import { PollingIndicator } from '../molecules/PollingIndicator';
import { PageSkeleton } from '../atoms/SkeletonCard';

export function AlertsPage() {
  const { selectedOperator } = useOperatorStore();

  const {
    summary,
    alerts,
    totalCount,
    selectedSeverities,
    isLoadingSummary,
    isLoadingAlerts,
    summaryError,
    alertsError,
    lastUpdate,
    loadAll,
    toggleSeverity,
    clearFilters,
    startPolling,
    stopPolling,
  } = useAlertsStore();

  // Initialize on mount
  useEffect(() => {
    if (selectedOperator?.operatorId) {
      loadAll();
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [selectedOperator?.operatorId]);

  const handleRefresh = () => {
    loadAll();
  };

  // Show skeleton on initial load
  if (isLoadingSummary && !summary) {
    return <PageSkeleton />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        <h1 className="text-xl font-semibold text-gray-900">Alerts</h1>
        <PollingIndicator
          lastUpdated={lastUpdate}
          isRefreshing={isLoadingSummary || isLoadingAlerts}
          onRefresh={handleRefresh}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Error States */}
        {(summaryError || alertsError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            <p className="font-medium">Error loading alerts</p>
            <p className="text-sm mt-1">{summaryError || alertsError}</p>
          </div>
        )}

        {/* Summary Section */}
        <AlertsSummarySection summary={summary} isLoading={isLoadingSummary} />

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border">
          <SeverityFilter
            selectedSeverities={selectedSeverities}
            onToggle={toggleSeverity}
            onClear={clearFilters}
            counts={
              summary
                ? {
                    critical: summary.criticalCount,
                    warning: summary.warningCount,
                    info: summary.infoCount,
                  }
                : undefined
            }
          />
        </div>

        {/* Alerts Table */}
        <AlertsTable
          alerts={alerts}
          totalCount={totalCount}
          isLoading={isLoadingAlerts}
          error={alertsError}
        />
      </div>
    </div>
  );
}
